import { Link } from 'react-router';
import type { CommunityUpdateSummary } from '@/api/messaging';
import { useT } from '@/i18n/languageStore';
import { useContactProfiles } from '@/features/messaging/queries';
import { useSessionStore } from '@/state/sessionStore';
import { Avatar } from '@/ui/kit/Avatar';
import { Icon } from '@/ui/kit/Icon';
import { useMyStories, useStoryFeed } from './storyQueries';
import styles from './home.module.css';

/** The people who shared something in the last 24 hours, the ones with something new first. */
export function storyAuthors(
  feed: CommunityUpdateSummary[],
): { authorId: string; unseen: boolean }[] {
  const byAuthor = new Map<string, boolean>();
  for (const update of feed) {
    byAuthor.set(update.authorId, (byAuthor.get(update.authorId) ?? false) || !update.seen);
  }
  return [...byAuthor]
    .map(([authorId, unseen]) => ({ authorId, unseen }))
    .sort((a, b) => Number(b.unseen) - Number(a.unseen));
}

/**
 * The row of avatars at the top of Home: a way to share your own update, and one circle for each neighbor who shared
 * something in the last 24 hours. A coloured ring means there is something you have not seen; a tap plays it.
 */
export function StoryBar() {
  const t = useT();
  const me = useSessionStore((s) => s.member);
  const feed = useStoryFeed().data ?? [];
  const mine = useMyStories().data ?? [];
  const authors = storyAuthors(feed);
  const profiles = useContactProfiles(authors.map((a) => a.authorId));

  return (
    <nav aria-label={t('Updates from your connections')} className={styles.storyBar}>
      <ul className={styles.storyBarList}>
        <li>
          <Link
            to={mine.length > 0 ? '/stories' : '/stories?share=1'}
            className={styles.storyBarItem}
            aria-label={mine.length > 0 ? t('Your update') : t('Share an update')}
          >
            <span className={styles.storyRing} data-own>
              <Avatar
                name={me?.name ?? t('You')}
                seed={me?.id}
                photoUrl={me?.photoUrl}
                size={52}
                decorative
              />
              {mine.length === 0 ? (
                <span className={styles.storyPlus} aria-hidden="true">
                  <Icon name="plus" size={12} />
                </span>
              ) : null}
            </span>
            <span className={styles.storyName}>
              {mine.length > 0 ? t('Your update') : t('Share')}
            </span>
          </Link>
        </li>
        {authors.map(({ authorId, unseen }) => {
          const profile = profiles.get(authorId);
          const name = profile?.name ?? t("Neighbor");
          return (
            <li key={authorId}>
              <Link
                to={`/stories/view/${authorId}`}
                className={styles.storyBarItem}
                aria-label={
                  unseen ? t('{name}, new update', { name }) : t('{name}, seen', { name })
                }
              >
                <span className={styles.storyRing} data-unseen={unseen || undefined}>
                  <Avatar
                    name={name}
                    seed={authorId}
                    photoUrl={profile?.photoUrl}
                    size={52}
                    decorative
                  />
                </span>
                <span className={styles.storyName}>{name.split(' ')[0]}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
